import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
} from '@mui/material';
import FunctionsData from '../../../../Functions/FunctionsData';
import FunctionsConfig from '../../../../Functions/FunctionsConfig'; // Default import
import moment from 'moment'; // Import moment for date handling

// ShowPrice Component
const ShowPrice = ({ row, customError, dateSurgeryValid, a_surgery_amount }) => {
  return (
    <div style={{ margin: 10 }}>
      Current cost: NZD {a_surgery_amount}.&nbsp;
    </div>
  );
};

// ShowText Component
function ShowText({ col, label, row, updateValue, allow, valid, setValid, disabled }) {
  useEffect(() => {
    if (!valid.find((v) => v.slug === col)) {
      setValid([
        ...valid,
        { slug: col, value: row[col] !== '' || (allow && !allow.includes(row[col])) }
      ]);
    }
  }, [col, row, allow, valid, setValid]);

  return (
    <div style={{ marginBottom: !allow ? 20 : null }}>
      <TextField
        error={row[col] === '' || (allow && !allow.includes(row[col]))}
        value={row[col] || ''}
        disabled={disabled}
        onChange={(e) => {
          updateValue(col, e.target.value);
          let valCopy = valid.filter((v) => v.slug !== col);
          setValid([
            ...valCopy,
            { slug: col, value: e.target.value !== '' && (!allow || allow.includes(e.target.value)) },
          ]);
        }}
        placeholder={label}
        fullWidth
      />
      {allow && (
        <div style={{ fontSize: '0.7em', marginBottom: 20 }}>
          Allowed values: {allow.join(', ')}
        </div>
      )}
    </div>
  );
}

export default function LandingCommunityLogsUpdateIssued({ row, setLoadingVouchers, setOpen, mode }) {
  const [state, setState] = useState({ ...row });
  const [valid, setValid] = useState([]);

  // State variables for dropdowns and options
  const [coloursPrimary, setColoursPrimary] = useState([]);
  const [coloursSecondary, setColoursSecondary] = useState([]);
  const [animalTypes, setAnimalTypes] = useState([]);
  const [weights, setWeights] = useState([]);
  const [sources, setSources] = useState([]);
  const [community, setCommunity] = useState(false); // Set this based on your app logic

  // Additional state for pricing
  const [a_surgery_amount, seta_surgery_amount] = useState(row.a_surgery_amount || 0.00);

  // Date of Procedure State
  const [dateSurgery, setDateSurgery] = useState(
    row.a_date_surgery ? moment(row.a_date_surgery).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
  );
  const [dateSurgeryValid, setDateSurgeryValid] = useState(false);

  useEffect(() => {
    if (row) setState({ ...row });
    setCommunity(
      (
          row.campaign_target &&
          (row.campaign_target.includes('community') || row.campaign_target.includes('inspectors') || row.campaign_target.includes('outreach'))
      ))
  }, [row]);

  useEffect(() => {
    // Fetch necessary data for dropdowns and options
    FunctionsConfig.getPrimaryColours(state.a_type).then((r) => {
      setColoursPrimary(r);
    });

    FunctionsConfig.getSecondaryColours(state.a_type).then((r) => {
      setColoursSecondary(r);
    });

    if (community) {
      FunctionsConfig.getAnimalTypesArray('species-community').then((r) => {
        setAnimalTypes(r);
      });
    } else {
      FunctionsConfig.getAnimalTypesArray('species-community').then((r) => {
        setAnimalTypes(r);
      });
    }

    FunctionsConfig.getWeights().then((r) => {
      setWeights(r);
    });

    FunctionsConfig.getReasonsSource(state.a_type).then((r) => {
      setSources(r);
    });
  }, [state.a_type, community]);

  // Date Validation
  useEffect(() => {
    const isValid =
      moment(row.created_at).isSameOrBefore(moment(dateSurgery)) &&
      moment(dateSurgery).isSameOrBefore(moment()) &&
      moment(dateSurgery).isValid();

    setDateSurgeryValid(isValid);
  }, [dateSurgery, row.created_at]);

  // Calculate Amounts
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const arr = await FunctionsConfig.pricingMatrix(
          state.a_type,
          state.a_gender,
          state.a_weight,
          state.a_type_surgery,
          community
        );

        if (arr) {
          let a = parseFloat(arr.price) || 0;
          if (state.a_is_pregnant > 0) {
            a += parseFloat(arr.pregnant_add) || 0;
          }
          let sponsorTotal = 0;
          if (!community && state.sponsor_applicant_cost > 0) {
            a += parseFloat(state.sponsor_applicant_cost) || 0;
            if (
              String(state.a_type_surgery).trim() !== 'Microchip only' &&
              parseFloat(state.sponsor_microchip_extra_cost) > 0
            ) {
              a += parseFloat(state.sponsor_microchip_extra_cost) || 0;
              sponsorTotal =
                parseFloat(state.sponsor_applicant_cost) + parseFloat(state.sponsor_microchip_extra_cost);
            } else {
              sponsorTotal = parseFloat(state.sponsor_applicant_cost) || 0;
            }
          }
          seta_surgery_amount(a);
        } else {
          seta_surgery_amount(0);
        }
      } catch (error) {
        console.error("Error fetching pricing data:", error);
        seta_surgery_amount(0);
      }
    };

    fetchPricing();
  }, [
    state.a_type,
    state.a_gender,
    state.a_weight,
    state.a_type_surgery,
    state.a_is_pregnant,
    state.sponsor_applicant_cost,
    state.sponsor_microchip_extra_cost,
    community
  ]);

  function updateValue(col, val) {
    setState({ ...state, [col]: val });
  }

  function submitData() {
    // If mode is issued, use contact_ fields. Otherwise, use owner_ fields.
    const prefix = mode === 'issued' ? 'contact' : 'owner';

    FunctionsData.postData(
      FunctionsData.getApiUrl('/api/voucher/update/') + row.id,
      {
        [`${prefix}_first`]: state[`${prefix}_first`],
        [`${prefix}_last`]: state[`${prefix}_last`],
        [`${prefix}_email`]: state[`${prefix}_email`],
        [`${prefix}_phone`]: state[`${prefix}_phone`],
        [`${prefix}_postcode`]: state[`${prefix}_postcode`],
        [`${prefix}_street_number`]: state[`${prefix}_street_number`],
        [`${prefix}_street`]: state[`${prefix}_street`],
        [`${prefix}_suburb`]: state[`${prefix}_suburb`],
        [`${prefix}_city`]: state[`${prefix}_city`],
        [`${prefix}_formatted_address`]: [
          state[`${prefix}_street_number`],
          state[`${prefix}_street`],
          state[`${prefix}_suburb`],
          state[`${prefix}_city`],
          state[`${prefix}_postcode`],
        ]
          // Remove any empty/undefined parts
          .filter(Boolean)
          // Join with a space or comma, depending on your preference
          .join(' '),
        a_name: state.a_name,
        a_type: state.a_type,
        a_source: state.a_source,
        a_gender: state.a_gender,
        a_age: state.a_age,
        a_color_primary: state.a_color_primary,
        a_color_secondary: state.a_color_secondary,
        a_weight: state.a_weight,
        a_microchipped: state.a_microchipped,
        a_is_pregnant: state.a_is_pregnant,
        a_was_pregnant: state.a_was_pregnant,
        a_microchip_only_flag: state.a_microchip_only_flag,
        a_date_surgery: dateSurgeryValid ? dateSurgery : null,
      }
    )
      .then(() => {
        setLoadingVouchers(true);
        setOpen(false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  // Determine the prefix and labels based on mode
  const prefix = mode === 'issued' ? 'contact' : 'owner';
  const firstLabel = mode === 'issued' ? 'Contact First Name' : 'Owner First Name';
  const lastLabel = mode === 'issued' ? 'Contact Last Name' : 'Owner Last Name';
  const emailLabel = mode === 'issued' ? 'Contact Email' : 'Owner Email';
  const phoneLabel = mode === 'issued' ? 'Contact Phone' : 'Owner Phone';
  const postcodeLabel = mode === 'issued' ? 'Contact Postcode' : 'Owner Postcode';
  const streetNumberLabel = mode === 'issued' ? 'Contact Street Number' : 'Owner Street Number';
  const streetLabel = mode === 'issued' ? 'Contact Street' : 'Owner Street';
  const suburbLabel = mode === 'issued' ? 'Contact Suburb' : 'Owner Suburb';
  const cityLabel = mode === 'issued' ? 'Contact City' : 'Owner City';

  return (
    <div style={{ width: '100%' }}>
      <h1 style={{ marginBottom: 20 }}>{row?.sn}</h1>

      <ShowText
        col={`${prefix}_first`}
        label={firstLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_last`}
        label={lastLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_email`}
        label={emailLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_phone`}
        label={phoneLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        
        col={`${prefix}_postcode`}
        label={postcodeLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_street_number`}
        label={streetNumberLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_street`}
        label={streetLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_suburb`}
        label={suburbLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />
      <ShowText
        col={`${prefix}_city`}
        label={cityLabel}
        row={state}
        updateValue={updateValue}
        valid={valid}
        setValid={setValid}
      />

      {/* Submit and Cancel Buttons */}
      <div>
        <Button
          disabled={valid.filter((v) => v.value === false).length > 0}
          variant="contained"
          onClick={submitData}
        >
          Submit
        </Button>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
